import { InboxOutlined, LoadingOutlined } from '@ant-design/icons';
import { Card, Flex, Spin, Table, Tag, Typography, Upload } from 'antd';
import { useState } from 'react';
import { bulkContactAssign } from '../../redux/actions/contact';
import { getFileUrl, getUploadUrl, uploadFileToS3 } from '../../redux/actions/upload';
import { getFullName, getNewFileName } from '../../utils';
import CustomModal from '../CustomModal';

const { Dragger } = Upload;
const { Text } = Typography;

const BulkContactAssignee = ({ open, onCancel }) => {
    const [filePath, setFilePath] = useState(null);
    const [data, setData] = useState([]);
    const [unlinkData, setUnlinkData] = useState([]);
    const [missingCustomers, setMissingCustomers] = useState([]);

    const [loading, setLoading] = useState({
        type: '',
        status: false,
    });

    const processFilePath = async (path) => {
        try {
            const res = await bulkContactAssign({ filePath: path, preview: true });
            if (res?.ack) {
                setFilePath(path);
                setData(res?.data);
                setUnlinkData(res?.unlinked || null);
                setMissingCustomers(res?.missing || []);
            }
        } catch (err) {
            console.error('Error processing file path:', err);
        }
    };

    const handleChange = async ({ file }) => {
        if (!file) return;
        setLoading({ type: 'upload', status: true });

        try {
            const name = getNewFileName(file.name);
            const uploadUrl = await getUploadUrl(file, 'contacts', 'bulk-assign', name);
            await uploadFileToS3(uploadUrl, file, file.type);

            const uploadedFilePath = getFileUrl('contacts', 'bulk-assign', name);
            await processFilePath(uploadedFilePath);
        } catch (error) {
            console.error('Error uploading file:', error);
        } finally {
            setLoading({ type: 'upload', status: false });
        }
    };

    const handleFinish = async () => {
        try {
            setLoading({ type: 'submit', status: true });
            const res = await bulkContactAssign({ filePath, preview: false });

            if (res?.ack) {
                onCancel();
                setFilePath(false);
                setData(null);
            }
        } catch (error) {
            console.error('Error submitting bulk assignment:', error);
        } finally {
            setLoading({ type: 'submit', status: false });
        }
    };

    const columns = [
        {
            title: 'Code',
            dataIndex: 'customer_code',
            key: 'customer_code',
        },
        {
            title: 'Customer Name',
            dataIndex: 'customer_name',
            key: 'customer_name',
        },
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
        },
        {
            key: 'phone',
            title: 'Phone',
            dataIndex: 'phone',
        },
        {
            title: 'Current Assignee',
            dataIndex: 'assigned_user_name',
            key: 'assigned_user_name',
        },
        {
            title: 'Assigned User',
            dataIndex: 'assignee',
            key: 'assignee',
            width: 200,
            render: (_, record) => record && getFullName(record?.assignee),
        },
    ];

    const unlinkColumns = [
        {
            title: 'Customer Code',
            dataIndex: 'code',
            key: 'code',
        },
        {
            title: 'Customer Name',
            dataIndex: 'customerName',
            key: 'customerName',
        },
        {
            title: 'Phone',
            dataIndex: 'phone',
            key: 'phone',
        },
        {
            title: 'WhatsApp Number',
            dataIndex: 'invoice_whatsapp_number',
            key: 'invoice_whatsapp_number',
        },
    ];

    return (
        <CustomModal
            title="Assign Contacts"
            open={open}
            onCancel={onCancel}
            okText="Submit"
            okButtonProps={{
                disabled: !filePath,
                loading: loading?.type === 'submit' && loading?.status,
            }}
            onOk={handleFinish}
            width={filePath && data ? 1200 : 800}
        >
            {!filePath && (
                <Dragger
                    name="file"
                    multiple={false}
                    accept=".xlsx, .xls"
                    showUploadList={false}
                    onChange={handleChange}
                    beforeUpload={() => false}
                >
                    <Flex vertical align="center" justify="center" style={{ minHeight: 120 }}>
                        {loading?.type === 'upload' && loading?.status ? (
                            <Flex vertical align="center" gap={8}>
                                <Spin indicator={<LoadingOutlined spin />} size="large" />
                                <Text type="secondary">Uploading your file...</Text>
                            </Flex>
                        ) : (
                            <>
                                <InboxOutlined style={{ fontSize: 42 }} />
                                <Text strong style={{ marginTop: 8 }}>
                                    Click or drag Excel file to this area to upload
                                </Text>
                            </>
                        )}
                    </Flex>

                    <Text type="secondary" style={{ fontSize: 13 }}>
                        Supports only Excel files (<b>.xls</b>, <b>.xlsx</b>). Make sure the file contains{' '}
                        <b>code</b> and <b>assigned_user_id</b> columns. This is for bulk reassignment of
                        customers and their contacts.
                    </Text>
                </Dragger>
            )}

            {filePath && !!data?.length && (
                <>
                    <Typography.Title level={5} className="mt-4">
                        Preview of Contacts to be Reassigned
                    </Typography.Title>
                    <Table
                        columns={columns}
                        dataSource={data}
                        rowKey="id"
                        components={{
                            header: {
                                cell: (props) => <th {...props} className="table-header px-2" />,
                            },
                            body: {
                                cell: (props) => <td {...props} className="px-2 py-1" />,
                            },
                        }}
                        rowClassName={(record) =>
                            record.assigned_user_id !== record?.assignee?.id ? 'updated-row' : ''
                        }
                        pagination={false}
                        scroll={{ x: 'max-content', y: 'calc(100dvh - 360px)' }}
                    />
                </>
            )}

            {filePath && !!unlinkData.length && (
                <>
                    <Typography.Title level={5} className="mt-4" type="warning">
                        Customers Without Linked Contacts (Not Updated)
                    </Typography.Title>
                    <Table
                        columns={unlinkColumns}
                        dataSource={unlinkData}
                        rowKey="id"
                        components={{
                            header: {
                                cell: (props) => <th {...props} className="table-header px-2" />,
                            },
                            body: {
                                cell: (props) => <td {...props} className="px-2 py-1" />,
                            },
                        }}
                        pagination={false}
                    />
                </>
            )}

            {filePath && !!missingCustomers?.length && (
                <Card
                    title={
                        <Typography.Title level={5} type="danger" className='m-0'>
                            {missingCustomers?.length} Customers Not Found in the System
                        </Typography.Title>
                    }
                    bordered={false}
                    className='mt-4'
                >
                    <Flex wrap="wrap">
                        {missingCustomers.map((customer) => (
                            <Tag key={customer.code}>
                                {customer.code}
                            </Tag>
                        ))}
                    </Flex>
                </Card>
            )}
        </CustomModal>
    );
};

export default BulkContactAssignee;
